import { parsePhoneNumber, isValidPhoneNumber, formatIncompletePhoneNumber } from 'libphonenumber-js'

/**
 * Utility class for phone number validation, formatting, and manipulation
 * Provides consistent phone handling across the Go Sit Back platform
 */
export class PhoneUtils {
  /**
   * Validates a phone number for a given country
   * Uses a more lenient validation approach that checks if the number is parseable
   * @param phone - The phone number to validate
   * @param country - The country code (defaults to 'US')
   * @returns boolean indicating if the phone number is valid
   */
  static validate(phone: string, country: string = 'US'): boolean {
    try {
      const parsed = parsePhoneNumber(phone, country as never)
      // For better UX, we consider a number valid if it's parseable and has the right length
      return parsed && parsed.number.length >= 10
    } catch {
      return false
    }
  }

  /**
   * Strict validation using the library's built-in validation
   * @param phone - The phone number to validate
   * @param country - The country code (defaults to 'US')
   * @returns boolean indicating if the phone number is strictly valid
   */
  static validateStrict(phone: string, country: string = 'US'): boolean {
    try {
      return isValidPhoneNumber(phone, country as never)
    } catch {
      return false
    }
  }

  /**
   * Formats a complete phone number
   * @param phone - The phone number to format
   * @param country - The country code (defaults to 'US')
   * @returns formatted phone number or original string if invalid
   */
  static format(phone: string, country: string = 'US'): string {
    try {
      const phoneNumber = parsePhoneNumber(phone, country as never)
      return phoneNumber ? phoneNumber.formatNational() : phone
    } catch {
      return phone
    }
  }

  /**
   * Formats an incomplete phone number as user types
   * @param phone - The partial phone number
   * @param country - The country code (defaults to 'US')
   * @returns formatted partial phone number
   */
  static formatIncomplete(phone: string, country: string = 'US'): string {
    return formatIncompletePhoneNumber(phone, country as never)
  }

  /**
   * Gets validation error message for a phone number
   * @param phone - The phone number to validate
   * @param country - The country code (defaults to 'US')
   * @returns error message or null if valid
   */
  static getValidationError(phone: string, country: string = 'US'): string | null {
    if (!phone.trim()) return 'Phone number is required'
    if (!this.validate(phone, country)) return 'Please enter a valid phone number'
    return null
  }

  /**
   * Extracts just the digits from a phone number
   * @param phone - The phone number
   * @returns string containing only digits
   */
  static extractDigits(phone: string): string {
    return phone.replace(/\D/g, '')
  }

  /**
   * Converts phone number to E.164 format for storage
   * @param phone - The phone number to convert
   * @param country - The country code (defaults to 'US')
   * @returns E.164 formatted number or null if invalid
   */
  static toE164(phone: string, country: string = 'US'): string | null {
    try {
      const phoneNumber = parsePhoneNumber(phone, country as never)
      return phoneNumber ? phoneNumber.number : null
    } catch {
      return null
    }
  }

  /**
   * Checks if a phone number appears to be from the US
   * @param phone - The phone number to check
   * @returns boolean indicating if it's a US number
   */
  static isUSNumber(phone: string): boolean {
    const countryCode = this.getCountryCode(phone)
    return countryCode === 'US'
  }

  /**
   * Gets the country code from a phone number
   * @param phone - The phone number
   * @param defaultCountry - Default country to assume if not specified (defaults to 'US')
   * @returns country code or null if unable to determine
   */
  static getCountryCode(phone: string, defaultCountry: string = 'US'): string | null {
    try {
      // First try without country context (for international numbers with + prefix)
      if (phone.startsWith('+')) {
        const phoneNumber = parsePhoneNumber(phone)
        if (phoneNumber && phoneNumber.country) {
          return phoneNumber.country
        }
      }
      
      // Try with default country for local numbers
      const phoneNumber = parsePhoneNumber(phone, defaultCountry as never)
      return phoneNumber ? phoneNumber.country || null : null
    } catch {
      return null
    }
  }
}
